/* =========================================================
   Pro Team Gallery — DermX

   Un único reveal de disparo único (sin pin ni scrub): las 3 fotos entran
   con fade-in + slide-up y un stagger entre ellas. Como el stagger sigue el
   orden del DOM, en escritorio va de izquierda a derecha y en móvil de
   arriba a abajo (apiladas) o de izquierda a derecha (carrusel) sin lógica
   extra. En el carrusel, los slides que están fuera de pantalla también
   completan su reveal, así que al deslizar ya están visibles.

   El carrusel en sí no depende de este archivo: lo gestiona
   <slider-component> de Dawn (global.js).

   toggleActions "play none none reverse": si el usuario sube por encima del
   punto de disparo y vuelve a bajar, el reveal se repite.

   Con prefers-reduced-motion: reduce no se crea ningún ScrollTrigger — el CSS
   ya deja las fotos visibles.

   Comparte con el resto de secciones la promesa window.dermxGsapReady, así
   que GSAP se descarga una única vez por página.
   ========================================================= */

(function () {
  'use strict';

  /* En el Theme Editor la sección re-inyecta su <script> y este archivo se
     vuelve a ejecutar. Si ya hay un módulo vivo, reutilizamos el suyo. */
  if (window.DermxProTeamGallery) {
    window.DermxProTeamGallery.initAll();
    return;
  }

  var GSAP_VERSION = '3.13.0';
  var GSAP_URL = 'https://cdnjs.cloudflare.com/ajax/libs/gsap/' + GSAP_VERSION + '/gsap.min.js';
  var SCROLLTRIGGER_URL =
    'https://cdnjs.cloudflare.com/ajax/libs/gsap/' + GSAP_VERSION + '/ScrollTrigger.min.js';

  var MOTION_OK = '(prefers-reduced-motion: no-preference)';
  var ROOT_SELECTOR = '.pro-team-gallery[data-section-id]';

  var STAGGER = 0.15;
  var DURATION = 0.7;
  var OFFSET_Y = 24; // fallback: el valor real es --pro-team-gallery-reveal-offset

  /* Una instancia por section.id: { mm } */
  var instances = new Map();

  /* ---------------------------------------------------------
     Carga de GSAP + ScrollTrigger (una sola vez por página)
     --------------------------------------------------------- */

  function loadScript(src) {
    return new Promise(function (resolve, reject) {
      var existing = document.querySelector('script[data-gsap-src="' + src + '"]');

      if (existing) {
        if (existing.dataset.loaded === 'true') {
          resolve();
        } else {
          existing.addEventListener('load', function () {
            resolve();
          });
          existing.addEventListener('error', reject);
        }
        return;
      }

      var script = document.createElement('script');
      script.src = src;
      script.async = false; // preserva el orden gsap -> ScrollTrigger
      script.dataset.gsapSrc = src;
      script.addEventListener('load', function () {
        script.dataset.loaded = 'true';
        resolve();
      });
      script.addEventListener('error', reject);
      document.head.appendChild(script);
    });
  }

  function isPluginRegistered(name) {
    return !!(window.gsap && window.gsap.core && window.gsap.core.globals()[name]);
  }

  function ensureGsap() {
    if (window.dermxGsapReady) return window.dermxGsapReady;

    window.dermxGsapReady = Promise.resolve()
      .then(function () {
        if (!window.gsap) return loadScript(GSAP_URL);
        return null;
      })
      .then(function () {
        if (!window.gsap) throw new Error('[pro-team-gallery] GSAP no disponible');
        if (!window.ScrollTrigger && !isPluginRegistered('ScrollTrigger')) {
          return loadScript(SCROLLTRIGGER_URL);
        }
        return null;
      })
      .then(function () {
        var gsap = window.gsap;
        var ScrollTrigger = window.ScrollTrigger || gsap.core.globals().ScrollTrigger;

        if (!ScrollTrigger) throw new Error('[pro-team-gallery] ScrollTrigger no disponible');
        if (!isPluginRegistered('ScrollTrigger')) gsap.registerPlugin(ScrollTrigger);

        ScrollTrigger.config({ ignoreMobileResize: true });

        return { gsap: gsap, ScrollTrigger: ScrollTrigger };
      });

    return window.dermxGsapReady;
  }

  /* ---------------------------------------------------------
     Utilidades
     --------------------------------------------------------- */

  var refreshTimer = null;

  function refreshSoon(ScrollTrigger) {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(function () {
      ScrollTrigger.refresh();
    }, 60);
  }

  /* Las imágenes lazy que aún no han cargado pueden mover el punto de
     disparo de lo que va debajo: refrescamos cuando llegan. */
  function refreshOnMedia(root, ScrollTrigger) {
    root.querySelectorAll('img').forEach(function (img) {
      if (img.complete) return;
      img.addEventListener(
        'load',
        function () {
          refreshSoon(ScrollTrigger);
        },
        { once: true }
      );
    });
  }

  /* El CSS reserva padding para el slide-up dentro del carrusel; leer el
     mismo valor evita que la animación y ese hueco se desincronicen. */
  function revealOffset(root) {
    var value = parseFloat(getComputedStyle(root).getPropertyValue('--pro-team-gallery-reveal-offset'));
    return isNaN(value) ? OFFSET_Y : value;
  }

  /* ---------------------------------------------------------
     Ciclo de vida por sección
     --------------------------------------------------------- */

  function destroy(sectionId) {
    var instance = instances.get(sectionId);
    if (!instance) return;

    // revert() mata el tween y su ScrollTrigger y devuelve los estilos inline:
    // sin esto, editar en vivo dejaría triggers duplicados.
    instance.mm.revert();
    instances.delete(sectionId);
  }

  function init(root) {
    if (!root) return;

    var sectionId = root.dataset.sectionId;
    if (!sectionId) return;

    destroy(sectionId); // evita duplicados al re-renderizar en el editor

    ensureGsap()
      .then(function (lib) {
        // El editor puede haber quitado la sección mientras cargaba el CDN.
        if (!root.isConnected) return;
        build(root, sectionId, lib.gsap, lib.ScrollTrigger);
      })
      .catch(function (error) {
        // Sin GSAP, las fotos se muestran directamente.
        root.classList.add('pro-team-gallery--static');
        console.warn(error);
      });
  }

  function build(root, sectionId, gsap, ScrollTrigger) {
    var grid = root.querySelector('.pro-team-gallery__grid');
    var items = root.querySelectorAll('[data-pro-team-gallery-item]');
    if (!grid || !items.length) return;

    var mm = gsap.matchMedia();
    instances.set(sectionId, { mm: mm });

    /* Sin contexto para movimiento reducido: ahí manda el CSS. */
    mm.add(MOTION_OK, function () {
      var tween = gsap.fromTo(
        items,
        { autoAlpha: 0, y: revealOffset(root) },
        {
          autoAlpha: 1,
          y: 0,
          duration: DURATION,
          ease: 'power2.out',
          stagger: STAGGER,
          overwrite: 'auto',
          scrollTrigger: {
            id: 'pro-team-gallery-' + sectionId,
            trigger: grid,
            start: 'top 80%',
            toggleActions: 'play none none reverse',
          },
        }
      );

      return function () {
        if (tween.scrollTrigger) tween.scrollTrigger.kill(true);
        tween.kill();
      };
    });

    refreshOnMedia(root, ScrollTrigger);
    refreshSoon(ScrollTrigger);
  }

  /* ---------------------------------------------------------
     Arranque + eventos del Theme Editor
     --------------------------------------------------------- */

  function initAll() {
    document.querySelectorAll(ROOT_SELECTOR).forEach(init);
  }

  function refreshIfReady() {
    if (window.gsap && isPluginRegistered('ScrollTrigger')) {
      refreshSoon(window.gsap.core.globals().ScrollTrigger);
    }
  }

  window.DermxProTeamGallery = { init: init, initAll: initAll, destroy: destroy };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  window.addEventListener('load', refreshIfReady);

  document.addEventListener('shopify:section:load', function (event) {
    var root = event.target.querySelector(ROOT_SELECTOR);
    if (!root) return;
    init(root);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    var root = event.target.querySelector(ROOT_SELECTOR);
    if (!root) return;
    destroy(root.dataset.sectionId);
  });

  // Al seleccionar/deseleccionar en el editor cambian offsets y alturas.
  ['shopify:section:select', 'shopify:section:deselect', 'shopify:block:select'].forEach(function (
    eventName
  ) {
    document.addEventListener(eventName, refreshIfReady);
  });
})();
